import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import "../../Styles/UserAuthStyles/VerifyEmail.css";
import { useUser } from "../../Context/UserContext";
import { api } from "../../api";

type VerifyState = "loading" | "success" | "already_verified" | "error" | "manual";

const RESEND_COOLDOWN_SECONDS = 60;

function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchUser } = useUser();

  const tokenFromURL = searchParams.get("token");
  const emailFromURL = searchParams.get("email");

  const [state, setState] = useState<VerifyState>(
    tokenFromURL && emailFromURL ? "loading" : "manual"
  );
  const [errorMessage, setErrorMessage] = useState("");

  // Manual form state (legacy fallback)
  const [email, setEmail] = useState(emailFromURL || "");
  const [code, setCode] = useState("");

  // Resend state
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<"idle" | "success" | "error">("idle");
  const [resendMessage, setResendMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Guard against React strict mode double-fire
  const verifyCalledRef = useRef(false);

  // Start cooldown timer
  const startCooldown = useCallback(() => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Cleanup cooldown interval on unmount
  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  // Resend verification by calling login endpoint (triggers re-send for unverified users)
  const handleResendVerification = async () => {
    // Determine which email to use
    const resendEmail = emailFromURL || email;
    if (!resendEmail || !resendEmail.trim()) {
      setResendStatus("error");
      setResendMessage("Please enter your email address first.");
      return;
    }

    setResending(true);
    setResendStatus("idle");
    setResendMessage("");

    try {
      await api.post("/api/resend-verification", {
        email: resendEmail.trim(),
      });
      // Always returns 200 with a generic message (doesn't leak account info)
      setResendStatus("success");
      setResendMessage("If an account with that email exists and is unverified, a new verification link has been sent.");
      startCooldown();
    } catch (err: any) {
      setResendStatus("error");
      setResendMessage("Something went wrong. Please try again later.");
    } finally {
      setResending(false);
    }
  };

  // Auto-verify when token + email are in the URL
  useEffect(() => {
    if (!tokenFromURL || !emailFromURL) return;
    if (verifyCalledRef.current) return;
    verifyCalledRef.current = true;

    const verify = async () => {
      try {
        const result = await api.get(
          `/api/verify-email?token=${encodeURIComponent(tokenFromURL)}&email=${encodeURIComponent(emailFromURL)}`
        );

        if (result.data === "Success:EmailVerified") {
          setState("success");
          await fetchUser();
        } else if (result.data === "Success:AlreadyVerified") {
          setState("already_verified");
          await fetchUser();
        } else {
          setState("error");
          setErrorMessage(
            result.data === "Fail:InvalidOrExpiredToken"
              ? "This verification link has expired or is invalid."
              : "Something went wrong. Please try again."
          );
        }
      } catch (err: any) {
        console.error("[verify-email] Error:", err?.response?.data || err?.message || err);
        setState("error");
        const serverMsg = err?.response?.data;
        if (serverMsg === "Success:AlreadyVerified") {
          setState("already_verified");
          await fetchUser();
          return;
        } else if (serverMsg === "Fail:InvalidOrExpiredToken") {
          setErrorMessage("This verification link has expired or is invalid.");
        } else if (err?.response?.status) {
          setErrorMessage("Something went wrong on the server. Please try again or log in to resend a new link.");
        } else {
          setErrorMessage("Network error. Please check your connection and try again.");
        }
      }
    };

    verify();
  }, [tokenFromURL, emailFromURL, fetchUser]);

  // Legacy manual code submission
  const handleManualVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const result = await api.post("/api/verify-email", { email, code });

      if (result.data === "Success:EmailVerified") {
        await fetchUser();
        toast.success("Email verified!");
        navigate("/dashboard");
      } else if (result.data === "Fail:InvalidOrExpiredToken") {
        toast.error("Invalid or expired verification code.");
      } else {
        toast.error("Please fill in all fields.");
      }
    } catch {
      toast.error("Network error during verification.");
    }
  };

  const resendButtonDisabled = resending || cooldown > 0;

  return (
    <div className="verify-shell">
      <div className="verify-bg-overlay" />

      <div className="verify-card">
        {/* Loading state */}
        {state === "loading" && (
          <div className="verify-content">
            <div className="verify-spinner" />
            <h2 className="verify-title">Verifying your email</h2>
            <p className="verify-subtitle">Just a moment...</p>
          </div>
        )}

        {/* Success state */}
        {state === "success" && (
          <div className="verify-content">
            <div className="verify-icon-circle verify-icon-success">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#ffffff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="verify-title">Email verified</h2>
            <p className="verify-subtitle">
              Your account is all set. You can now access your dashboard and start
              sending documents for signature.
            </p>
            <button
              className="verify-primary-btn"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8h10M9 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Already verified state */}
        {state === "already_verified" && (
          <div className="verify-content">
            <div className="verify-icon-circle verify-icon-success">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#ffffff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="verify-title">Already verified</h2>
            <p className="verify-subtitle">
              Your email has already been verified. You can go straight to your
              dashboard.
            </p>
            <button
              className="verify-primary-btn"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8h10M9 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Error state */}
        {state === "error" && (
          <div className="verify-content">
            <div className="verify-icon-circle verify-icon-error">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="#ffffff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="verify-title">Verification failed</h2>
            <p className="verify-subtitle">{errorMessage}</p>
            <div className="verify-btn-group">
              <button
                className="verify-primary-btn"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </button>
              <button
                className="verify-secondary-btn"
                onClick={() => navigate("/signup")}
              >
                Create New Account
              </button>
            </div>

            {/* Resend verification button */}
            {emailFromURL && (
              <>
                <button
                  type="button"
                  className="verify-secondary-btn"
                  disabled={resendButtonDisabled}
                  onClick={handleResendVerification}
                  style={{ marginTop: 12 }}
                >
                  {resending
                    ? "Sending..."
                    : cooldown > 0
                    ? `Resend Email (${cooldown}s)`
                    : "Resend Verification Email"}
                </button>
                {resendStatus !== "idle" && (
                  <p
                    className={`verify-resend-status ${
                      resendStatus === "success"
                        ? "verify-resend-status--success"
                        : "verify-resend-status--error"
                    }`}
                  >
                    {resendMessage}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Manual verification form (legacy) */}
        {state === "manual" && (
          <div className="verify-content">
            <div className="verify-icon-circle verify-icon-info">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 8l9-5 9 5v8l-9 5-9-5V8z"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 12v4M12 8h.01"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h2 className="verify-title">Verify your email</h2>
            <p className="verify-subtitle">
              Enter your email and the verification code we sent you.
            </p>

            <form className="verify-form" onSubmit={handleManualVerify}>
              <label className="verify-field">
                <span className="verify-label">Email</span>
                <input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </label>

              <label className="verify-field">
                <span className="verify-label">Verification code</span>
                <input
                  type="text"
                  placeholder="Enter your code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoComplete="one-time-code"
                  required
                />
              </label>

              <button type="submit" className="verify-primary-btn">
                Verify Email
              </button>
            </form>

            <p className="verify-footer">
              {"Didn't receive an email? "}
              <button
                type="button"
                className="verify-link"
                disabled={resendButtonDisabled || !email.trim()}
                onClick={handleResendVerification}
              >
                {resending
                  ? "Sending..."
                  : cooldown > 0
                  ? `Resend (${cooldown}s)`
                  : "Resend verification email"}
              </button>
            </p>
            {resendStatus !== "idle" && (
              <p
                className={`verify-resend-status ${
                  resendStatus === "success"
                    ? "verify-resend-status--success"
                    : "verify-resend-status--error"
                }`}
              >
                {resendMessage}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VerifyEmail;
